// components/landingPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle, Book, RotateCcw, AlertCircle } from 'lucide-react';
import { submitEmailToAPI } from '../utils/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setIsLoading(true);
      setError('');
      
      const result = await submitEmailToAPI(email);
      
      setIsLoading(false);
      
      if (result.success) {
        setIsSubscribed(true);
        console.log('Successfully subscribed:', result.data);
      } else {
        console.error('Subscription failed:', result.error);
        if (result.error.includes('reCAPTCHA')) {
          setError('Security verification failed. Please try again.');
        } else {
          setError('Failed to subscribe. Please try again.');
        }
      }
    }
  };

  const startQuiz = () => {
    // Track quiz start event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'quiz_started', {
        'source': 'landing_page'
      });
    }
    navigate('/quiz');
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
            Refresh Your CPR
            <span className="text-indigo-600"> Knowledge</span>
          </h2>
          <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
            When the time comes to perform CPR, you don't want to be second guessing yourself. 
            To help keep your CPR knowledge front of mind and ready when you need it, we'll send 
            you a reminder monthly to complete a short quiz to test yourself.
          </p>

          {/* Email Subscription Box */}
          <div id="subscription-section" className="mt-12 max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 border">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                Get Monthly Reminders
              </h3>
              <p className="text-gray-600 mb-6 text-center">
                Never forget to refresh your CPR knowledge. We'll send you a monthly reminder to take the quiz.
              </p>
              
              {!isSubscribed ? (
                <form onSubmit={handleSubscribe} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      isLoading
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                    }`}
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Subscribing...
                      </span>
                    ) : (
                      'Subscribe for Reminders'
                    )}
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700">
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>
                </form>
              ) : (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="text-green-600 font-medium">
                    Thanks for subscribing! You'll receive monthly reminders at {email}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-8">
            <button
              onClick={startQuiz}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Start Quiz
              <ChevronRight className="ml-2 h-5 w-5" />
            </button>
          </div>

          {/* Features */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="h-12 w-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto">
                <CheckCircle className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-gray-900">10 Random Questions</h3>
              <p className="mt-2 text-gray-600">Each quiz features 10 randomly selected questions from our comprehensive database.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto">
                <Book className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-gray-900">Instant Feedback</h3>
              <p className="mt-2 text-gray-600">Get detailed explanations and sources for each answer to enhance your learning.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto">
                <RotateCcw className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-gray-900">Retake Anytime</h3>
              <p className="mt-2 text-gray-600">Practice makes perfect! Retake the quiz as many times as you want.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;